import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '../store'
import { IntegrationType, WidgetMode } from '../models/enums'
import { done as treatmentScreenDone } from './treatmentScreen/treatmentScreenSlice'
import { done as cityScreenDone } from './cityScreenSlice'
import {
  done as clinicScreenDone,
  setSelectedClinic,
} from './clinicScreen/clinicScreenSlice'
import { done as appointmentScreenDone } from './appointmentScreen/appointmentScreenSlice'
import { done as bookingSummaryScreenDone } from './bookingSummaryScreenSlice'
import { done as bookingFlowDone } from './bookingFlow/bookingFlowSlice'
import { done as myBookingsDone } from './myBookingsSlice'
import { done as yourBookingsScreenDone } from './yourBookingsSlice'
import { done as bookingDetailsScreenDone } from './bookingDetails/bookingDetailsSlice'
import { mockRouter, router } from '../router'
import { Screens } from 'patient-api-lib'

interface ScreensRouterState {
  currentScreen: Screens | null
  navigationStack: Screens[]
  autoNavigation: boolean
}

export const initialState: ScreensRouterState = {
  currentScreen: null,
  navigationStack: [],
  autoNavigation: true,
}

export const navigateBackTo = createAsyncThunk<
  void,
  Screens,
  { dispatch: AppDispatch; state: RootState }
>('screensRouter', (screen, thunkApi) => {
  const currentScreen = thunkApi.getState().screensRouter.currentScreen
  const mode = thunkApi.getState().app.mode
  const integrationType = thunkApi.getState().app.integrationType
  if (!currentScreen) return

  const navigationStack = thunkApi.getState().screensRouter.navigationStack
  const currentScreenPosition = navigationStack.indexOf(currentScreen)
  const destinationScreenPosition = navigationStack.indexOf(screen)
  if (destinationScreenPosition === -1 || currentScreenPosition === -1) return
  const routeInstance =
    mode === WidgetMode.Embedded &&
    integrationType !== IntegrationType.PatientHub
      ? mockRouter
      : router

  if (navigationStack[destinationScreenPosition] === Screens.ClinicScreen) {
    thunkApi.dispatch(setSelectedClinic(null)) // TODO: Temporary solution for resetting clinic selection
  }

  routeInstance.navigate(destinationScreenPosition - currentScreenPosition)
})

const screensRouterSlice = createSlice({
  name: 'screensRouter',
  initialState,
  reducers: {
    setCurrentScreen: (state, action: PayloadAction<Screens>) => {
      state.currentScreen = action.payload
    },
    setNavigationStack: (state, action: PayloadAction<Screens>) => {
      const position = state.navigationStack.indexOf(action.payload)
      if (position === -1)
        state.navigationStack = [...state.navigationStack, action.payload]
      else state.navigationStack = state.navigationStack.slice(0, position + 1)
    },
    setAutoNavigation: (state, action: PayloadAction<boolean>) => {
      state.autoNavigation = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(treatmentScreenDone.fulfilled, (state, action) => {
      state.autoNavigation = true
      state.currentScreen = action.payload
    })

    builder.addCase(cityScreenDone.fulfilled, (state, action) => {
      state.autoNavigation = true
      state.currentScreen = action.payload
    })

    builder.addCase(clinicScreenDone.fulfilled, (state, action) => {
      state.autoNavigation = true
      state.currentScreen = action.payload
    })

    builder.addCase(appointmentScreenDone.fulfilled, (state, action) => {
      state.autoNavigation = true
      state.currentScreen = action.payload
    })

    builder.addCase(bookingSummaryScreenDone.fulfilled, (state, action) => {
      state.autoNavigation = true
      state.currentScreen = action.payload
    })

    builder.addCase(bookingFlowDone.fulfilled, (state, action) => {
      state.autoNavigation = true
      state.currentScreen = action.payload
    })

    builder.addCase(myBookingsDone.fulfilled, (state, action) => {
      state.autoNavigation = true
      state.currentScreen = action.payload
    })

    builder.addCase(yourBookingsScreenDone.fulfilled, (state, action) => {
      state.autoNavigation = true
      state.currentScreen = action.payload
    })

    builder.addCase(bookingDetailsScreenDone.fulfilled, (state, action) => {
      state.autoNavigation = true
      state.currentScreen = action.payload
    })
  },
})

export const { setCurrentScreen, setNavigationStack, setAutoNavigation } =
  screensRouterSlice.actions

export const selectCurrentScreen = (state: RootState) =>
  state.screensRouter.currentScreen

export const selectAutoNavigation = (state: RootState) =>
  state.screensRouter.autoNavigation

export const selectNavigationStack = (state: RootState) =>
  state.screensRouter.navigationStack

export const screensRouterReducer = screensRouterSlice.reducer
